// Active-store state. The user's claims carry a `storeAccess: []` array
// listing every store they can read in their tenant. We pick one as the
// "active" store, default to `claims.storeId`, persist the selection in
// localStorage per-user so it survives reloads, and broadcast changes via
// a simple event-emitter so subscribed pages re-query when it flips.
//
// Why an event-emitter vs. React context: pages already subscribe to
// `useAuth()` for the profile; layering another context for the active
// store turned out to be heavier than a tiny module-level singleton with
// a useSyncExternalStore hook. Render correctness is the same.

import { useSyncExternalStore } from "react";

const listeners = new Set();
let _active = null; // currently-active store id
let _key    = null; // localStorage key (scoped to user uid)
let _access = [];   // the user's allowed storeAccess set (for validation)

function emit() {
  for (const l of listeners) {
    try { l(); } catch (err) { /* swallow — one bad listener shouldn't kill others */ }
  }
}

function lsGet(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}
function lsSet(key, val) {
  try { localStorage.setItem(key, val); } catch { /* private mode etc — ignore */ }
}

// Called from AuthProvider once the profile lands. Picks the persisted
// store from localStorage if it's still in the user's storeAccess; falls
// back to claims.storeId; falls back to first entry; finally null.
export function bootActiveStore({ uid, storeId, storeAccess }) {
  _key = uid ? `sevric.battery.activeStore.${uid}` : null;
  const access = Array.isArray(storeAccess) ? storeAccess : (storeId ? [storeId] : []);
  _access = access;
  const saved  = _key ? lsGet(_key) : null;
  let next = null;
  if (saved && access.includes(saved)) next = saved;
  else if (storeId && access.includes(storeId)) next = storeId;
  else if (access.length > 0) next = access[0];
  else next = storeId || null;
  if (next !== _active) {
    _active = next;
    emit();
  }
}

// Force a switch. Persists to localStorage and notifies subscribers.
// Rejects any store the user doesn't have access to — a tampered localStorage
// value or a stray call can't point reads at a store outside storeAccess.
export function setActiveStore(storeId) {
  if (!storeId || storeId === _active) return;
  if (_access.length > 0 && !_access.includes(storeId)) {
    console.warn("[activeStore] ignored switch to a store outside storeAccess:", storeId);
    return;
  }
  _active = storeId;
  if (_key) lsSet(_key, storeId);
  emit();
}

export function getActiveStore() { return _active; }

// React hook. Re-renders when the active store changes.
export function useActiveStore() {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
    () => _active,
    () => _active,
  );
}
